import React, { useMemo, useState } from 'react';
import { Check, X, ChevronUp, ChevronDown, ChevronsUpDown, ExternalLink } from 'lucide-react';

const MainTable = ({
  files,
  isAssignedToFormerJudge,
  isAssignedToManualJudge,
  onFileClick,
  onJudgeClick,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const columns = useMemo(
    () => [
      { key: 'index', header: 'Sıra', sortable: false, headerAlign: 'right' },
      { key: 'esasNo', header: 'Esas No', sortable: true },
      { key: 'sanikSayisi', header: 'Sanık', sortable: true, headerAlign: 'center' },
      { key: 'magdurSayisi', header: 'Mağdur', sortable: true, headerAlign: 'center' },
      { key: 'klasorSayisi', header: 'Klasör', sortable: true },
      { key: 'gorus', header: 'T. Görüşü', sortable: true },
      { key: 'sucTuru', header: 'Suç Türü', sortable: true },
      { key: 'eskiHakim', header: 'Eski Hakim', sortable: true, alwaysShow: false },
      { key: 'manuelTevziEdilecekHakim', header: 'Manuel Tevzi', sortable: true, alwaysShow: false },
      { key: 'puan', header: 'Puan', sortable: true, sortKey: 'dosyaPuani', headerAlign: 'right' },
      { key: 'atananHakim', header: 'Atanan Hakim', sortable: true },
    ],
    []
  );

  const visibleColumns = useMemo(() => {
    return columns.filter((c) => {
      if (c.alwaysShow !== false) return true;
      // Sadece dolu alanı bulunan sütunları göster (eski hakim / manuel tevzi)
      return files.some((f) => {
        const v = f[c.key];
        return v !== null && v !== undefined && v !== '';
      });
    });
  }, [columns, files]);

  const sortedFiles = useMemo(() => {
    if (!sortConfig.key) return files;
    const key = sortConfig.key;
    const dir = sortConfig.direction === 'ascending' ? 1 : -1;
    return [...files].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), 'tr', { numeric: true }) * dir;
    });
  }, [files, sortConfig]);

  const requestSort = (column) => {
    if (!column.sortable) return;
    const key = column.sortKey || column.key;
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'ascending' ? 'descending' : 'ascending' };
      }
      return { key, direction: 'ascending' };
    });
  };

  const renderPuan = (file) => {
    const ham = Number(file.dosyaPuani);
    const alinan = Number(file.alinanPuan);
    const hasAlinan = Number.isFinite(alinan);
    const sameAsHam = hasAlinan && Math.abs(ham - alinan) < 1e-6;
    if (!hasAlinan || sameAsHam) {
      return Number.isFinite(ham) ? (
        <span className="tabular-nums">{ham.toFixed(2)}</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    }
    return (
      <span className="inline-flex items-baseline gap-1">
        <span className="tabular-nums text-slate-400 line-through">{ham.toFixed(2)}</span>
        <span className="tabular-nums font-semibold text-emerald-700">{alinan.toFixed(2)}</span>
      </span>
    );
  };

  const renderEskiHakim = (file) => {
    if (!file.eskiHakim) return <span className="text-slate-400">—</span>;
    const atandi = isAssignedToFormerJudge && isAssignedToFormerJudge(file);
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="truncate">{file.eskiHakim}</span>
        {atandi ? (
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white"
            title="Eski hakimine tevzi edildi (çarpan uygulanır)"
          >
            <Check size={12} strokeWidth={3} />
          </span>
        ) : (
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500"
            title="Eski hakimine tevzi edilmedi"
          >
            <X size={12} strokeWidth={3} />
          </span>
        )}
      </span>
    );
  };

  const renderManuelTevzi = (file) => {
    if (!file.manuelTevziEdilecekHakim) return <span className="text-slate-400">—</span>;
    const atandi = isAssignedToManualJudge && isAssignedToManualJudge(file);
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="truncate">{file.manuelTevziEdilecekHakim}</span>
        {atandi ? (
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white"
            title="Manuel atama uygulandı"
          >
            <Check size={12} strokeWidth={3} />
          </span>
        ) : (
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500"
            title="Manuel atama uygulanmadı"
          >
            <X size={12} strokeWidth={3} />
          </span>
        )}
      </span>
    );
  };

  const renderEsasNo = (file) => {
    const text = file.esasNo || '—';
    if (!onFileClick || !file.esasNo) return <span className="font-medium text-slate-700">{text}</span>;
    return (
      <button
        type="button"
        onClick={() => onFileClick(file)}
        className="inline-flex items-center gap-1 rounded text-left font-semibold text-indigo-700 transition hover:text-indigo-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
        title="Dosya detayını aç"
      >
        {text}
        <ExternalLink size={11} className="opacity-60" aria-hidden />
      </button>
    );
  };

  const renderAtananHakim = (file) => {
    if (!file.atananHakim) return <span className="text-slate-400">—</span>;
    if (!onJudgeClick) return <span className="font-medium text-slate-700">{file.atananHakim}</span>;
    return (
      <button
        type="button"
        onClick={() => onJudgeClick(file.atananHakim)}
        className="inline-flex items-center gap-1 rounded text-left font-semibold text-indigo-700 transition hover:text-indigo-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
        title="Hakim Detayları'na git"
      >
        {file.atananHakim}
        <ExternalLink size={11} className="opacity-60" aria-hidden />
      </button>
    );
  };

  const renderCell = (file, column, displayIndex) => {
    switch (column.key) {
      case 'index':
        return <span className="tabular-nums text-slate-400">{displayIndex + 1}</span>;
      case 'esasNo':
        return renderEsasNo(file);
      case 'eskiHakim':
        return renderEskiHakim(file);
      case 'manuelTevziEdilecekHakim':
        return renderManuelTevzi(file);
      case 'puan':
        return renderPuan(file);
      case 'atananHakim':
        return renderAtananHakim(file);
      case 'sanikSayisi':
      case 'magdurSayisi':
        return <span className="tabular-nums">{file[column.key] ?? '—'}</span>;
      default: {
        const v = file[column.key];
        return v == null || v === '' ? <span className="text-slate-400">—</span> : v;
      }
    }
  };

  const sortKeyOf = (column) => column.sortKey || column.key;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              {visibleColumns.map((column) => {
                const active = sortConfig.key === sortKeyOf(column);
                const align =
                  column.headerAlign === 'center'
                    ? 'justify-center text-center'
                    : column.headerAlign === 'right'
                      ? 'justify-end text-right'
                      : 'text-left';
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      active
                        ? sortConfig.direction === 'ascending'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                    onClick={() => requestSort(column)}
                    className={`whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ${column.sortable ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                  >
                    <span className={`inline-flex items-center gap-1 ${align}`}>
                      {column.header}
                      {column.sortable && (
                        active ? (
                          sortConfig.direction === 'ascending' ? (
                            <ChevronUp size={13} className="text-indigo-600" aria-hidden />
                          ) : (
                            <ChevronDown size={13} className="text-indigo-600" aria-hidden />
                          )
                        ) : (
                          <ChevronsUpDown size={12} className="text-slate-300" aria-hidden />
                        )
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedFiles.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="px-3 py-12 text-center text-sm italic text-slate-400"
                >
                  Henüz dosya yok.
                </td>
              </tr>
            ) : (
              sortedFiles.map((file, index) => (
                <tr
                  key={file.id}
                  className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-indigo-50/50"
                >
                  {visibleColumns.map((column) => {
                    const align =
                      column.headerAlign === 'center'
                        ? 'text-center'
                        : column.headerAlign === 'right'
                          ? 'text-right'
                          : 'text-left';
                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-2.5 text-sm text-slate-700 ${align}`}
                      >
                        {renderCell(file, column, index)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MainTable;
